// src/components/SearchOverlay.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SearchAddBar from './SearchAddBar';
import SearchResults from './SearchResults';
import CreateItemForm from './CreateItemForm';

const SearchOverlay = ({
  isOpen,
  onClose,
  context, // 'list' o 'catalog'
  allItems,
  onAddToList,
  onAddToCatalog,
  onEditItem,
  onDeleteItem,
  onQuantityChange,
  onCheckToggle,
  onChangeList,
  onUpdateItem, // NUEVA PROP
  showToast,
  availableCategories,
  activeList,
  activeListId,
  lists
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('search'); // 'search', 'create'
  const [isSaving, setIsSaving] = useState(false);
  const [createItemName, setCreateItemName] = useState('');
  
  const searchAddBarRef = useRef(null);
  const createNameInputRef = useRef(null);
  const createFormRef = useRef(null);

  // Reset cuando se abre
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setView('search');
      setIsSaving(false);
      setCreateItemName('');
      
      setTimeout(() => {
        if (searchAddBarRef.current) {
          searchAddBarRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen]);

  // Manejar tecla ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isSaving, onClose]);

  const handleContentClick = (e) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  const handleClear = () => {
    if (view === 'create') {
      setView('search');
      setSearchTerm('');
      setTimeout(() => {
        if (searchAddBarRef.current) {
          searchAddBarRef.current.focus();
        }
      }, 50);
    } else {
      onClose();
    }
  };

  const handleAddClick = (term) => {
    setCreateItemName(term);
    setView('create');
  };

  const handleCreateItem = async (itemData) => {
    setIsSaving(true);
    
    try {
      if (context === 'list') {
        await onAddToCatalog({
          ...itemData,
          quantity: 1,
          list_id: activeListId,
          list_name: activeList?.name || ''
        });
      } else {
        await onAddToCatalog({
          ...itemData,
          quantity: 0,
          list_id: null,
          list_name: ''
        });
      }
      
      showToast(t('toasts.itemCreated'), 'success');
      onClose();
      
    } catch (error) {
      showToast(t('toasts.errorCreating', { msg: error.message }), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelCreate = () => {
    setView('search');
    setSearchTerm('');
    setTimeout(() => {
      if (searchAddBarRef.current) {
        searchAddBarRef.current.focus();
      }
    }, 50);
  };

  const handleAddToListFromResults = async (itemId) => {
    try {
      await onAddToList(itemId);
      onClose();
    } catch (error) {
      showToast(t('toasts.errorAdding', { msg: error.message }), 'error');
    }
  };

  const handleCreateSave = () => {
    if (createFormRef.current) {
      createFormRef.current.submit();
    }
  };

  const handleCreateNameChange = (e) => {
    setCreateItemName(e.target.value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <div className="bg-white border-b border-gray-200 px-3 py-2">
        {view === 'search' ? (
          <SearchAddBar
            ref={searchAddBarRef}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onAdd={handleAddClick}
            onFocus={() => {}}
            onClear={handleClear}
            isSaving={isSaving}
            autoFocus={false}
          />
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                ref={createNameInputRef}
                type="text"
                value={createItemName}
                onChange={handleCreateNameChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && createItemName.trim() && !isSaving) {
                    handleCreateSave();
                  }
                }}
                placeholder={t('search.itemName')}
                disabled={isSaving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <button
              type="button"
              onClick={handleCancelCreate}
              disabled={isSaving}
              className="px-1 py-2 underline text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {t('common.cancel')}
            </button>

            <button
              type="button"
              onClick={handleCreateSave}
              disabled={!createItemName.trim() || isSaving}
              className={`px-3 py-2 font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2 ${
                createItemName.trim() && !isSaving
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  <span>{t('common.save')}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div 
        className="flex-1 overflow-y-auto bg-gray-50"
        onClick={handleContentClick}
      >
        {view === 'search' && (
          <SearchResults
            searchTerm={searchTerm}
            allItems={allItems}
            onAddToList={handleAddToListFromResults}
            onEditItem={onEditItem}
            onDeleteItem={onDeleteItem}
            onQuantityChange={onQuantityChange}
            onCheckToggle={onCheckToggle}
            onChangeList={onChangeList}
            onUpdateItem={onUpdateItem} // NUEVA PROP
            showToast={showToast}
            availableCategories={availableCategories}
            activeList={activeList}
            activeListId={activeListId}
            lists={lists}
            context={context}
          />
        )}

        {view === 'create' && (
          <CreateItemForm
            ref={createFormRef}
            initialName={createItemName}
            categories={availableCategories}
            onSave={handleCreateItem}
            onCancel={handleCancelCreate}
            isSaving={isSaving}
            context={context}
            activeList={activeList}
          />
        )}
      </div>
    </div>
  );
};

export default SearchOverlay;